import * as Sentry from '@sentry/browser';

import { ApiHelper, AuthHelper } from '../helpers';
import React, { FunctionComponent, PropsWithChildren, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { AuthContext } from '.';
import { Spinner } from '../utils';
import { UserType } from '../types';
import axios from 'axios';
import { useErrorHandler } from '../hooks';
import { useTranslation } from 'react-i18next';

const AuthProvider: FunctionComponent<PropsWithChildren> = ({ children }) => {
  const [searchParams] = useSearchParams();
  const [loadingInitial, setLoadingInitial] = useState<boolean>(true);
  const [authenticated, setAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<UserType>();
  const code = searchParams.get('code');
  const navigate = useNavigate();
  const errorHandler = useErrorHandler();
  const { t } = useTranslation();

  useEffect(() => {
    if (code) {
      axios(ApiHelper.getUrl.apiSCE.token(), {
        method: 'GET',
        params: {
          codigo: code,
          tipo: 'CODIGO',
          url: process.env.REACT_APP_REDIRECT_URL,
        },
      })
        .then((response) => {
          const responseUser = response.data;
          setUser(responseUser);
          setAuthenticated(true);
          sessionStorage.setItem('user', JSON.stringify(responseUser));
          Sentry.setUser(AuthHelper.extractSentryProps(responseUser));
          navigate('/eleicoes', { replace: true });
        })
        .catch((error) => {
          Sentry.captureException(error);
          errorHandler(error, t('erro.login'));
        })
        .finally(() => setLoadingInitial(false));
    }
  }, [code]);

  useEffect(() => {
    const user = sessionStorage.getItem('user');
    if (user) {
      const parsedUser = JSON.parse(user);
      setUser(parsedUser);
      Sentry.setUser(AuthHelper.extractSentryProps(parsedUser));
      setAuthenticated(true);
    }
    if (!code) {
      setLoadingInitial(false);
    }
  }, []);

  // Função usada exclusivamente para o login no ambiente de revisão.
  const login = (callback: VoidFunction) => {
    const fixedUser = AuthHelper.getFixedUser();
    setUser(fixedUser);
    setAuthenticated(true);
    sessionStorage.setItem('user', JSON.stringify(fixedUser));
    callback();
  };

  const logout = (callback: VoidFunction) => {
    setUser(undefined);
    setAuthenticated(false);
    sessionStorage.removeItem('user');
    Sentry.configureScope((scope) => scope.setUser(null));
    callback();
  };

  const value = {
    authenticated,
    login,
    logout,
    setAuthenticated,
    setUser,
    user,
  };

  return <AuthContext.Provider value={value}>{loadingInitial ? <Spinner /> : children}</AuthContext.Provider>;
};

export default AuthProvider;
